import { useState, useEffect, createContext, useContext } from 'react';
import { NavLink, useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  ListMusic,
  Clock,
  MessageSquare,
  BarChart3,
  Settings,
  Layers,
  AlertTriangle,
  Speaker,
  AlertCircle,
  ChevronRight,
  X,
  Home,
  Lock,
  Unlock,
  Heart,
  Palette,
  Shield,
  Trophy,
  Ticket,
  Crosshair,
  Brain,
  Info,
  Dices,
  PartyPopper,
  Award,
} from 'lucide-react';
import { GameDirectionsModal } from '../../components/games/GameDirectionsModal';
import { PEOPLES_PICK_HIDDEN, OVERFLOW_POOL_ENABLED } from '../../utils/featureFlags';
import PrizeManagePanel from '../../components/prizes/PrizeManagePanel';
import LotteryPanel from '../../components/games/LotteryPanel';
import PredictionPanel from '../../components/games/PredictionPanel';
import PredictionGameBanner from '../../components/games/PredictionGameBanner';
import TriviaPanel from '../../components/games/TriviaPanel';
import RoulettePanel from '../../components/games/RoulettePanel';
import DanceOffPanel from '../../components/games/DanceOffPanel';
import EventAwardsPanel from '../../components/games/EventAwardsPanel';
import { subscribeToRouletteConfig, RouletteConfig } from '../../services/roulette';
import { subscribeToOpenDanceOff, DanceOff } from '../../services/danceOff';
import { subscribeToEventAwards, type EventAwards } from '../../services/eventAwards';
import { useRouletteTimer } from '../../hooks/useRouletteTimer';
import { subscribeToPredictionGame, PredictionGame } from '../../services/prediction';
import { subscribeToTriviaGame, TriviaGame } from '../../services/trivia';
import { subscribeToLockedClues } from '../../services/triviaClues';
import { subscribeToDJPrizes } from '../../services/prizes';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../context/AuthContext';
import { computeEventStatus, docToEvent, getEventById, isDjEventOwner, Event, updateEvent, unlockEvent, unarchiveEvent, savePostEventData, PostEventData } from '../../services/events';
import { EventTierBadge } from '../../components/dj/EventTierBadge';
import { getVerifiedAttendeeCount } from '../../services/verification';
import { getRequestsByEventId, SongRequest } from '../../services/requests';
import { checkQueueVariety, getSuggestedGenres, calculateGenreDistribution, GENRE_COLORS } from '../../utils/genreUtils';
import QueuePage from './QueuePage';
import PendingPage from './PendingPage';
import MessagesPage from './MessagesPage';
import AnalyticsPage from './AnalyticsPage';
import FeedbackPage from './FeedbackPage';
import EventSettingsPage from './EventSettingsPage';
import PrepAheadPage from './PrepAheadPage';
import toast from 'react-hot-toast';

// Context for sharing event data with child tab pages (replaces useOutletContext)
interface EventManageContextType {
  event: Event;
  setEvent: React.Dispatch<React.SetStateAction<Event | null>>;
}

const EventManageContext = createContext<EventManageContextType | null>(null);

export function useEventManageContext() {
  const ctx = useContext(EventManageContext);
  if (!ctx) throw new Error('useEventManageContext must be used within EventManagePage');
  return ctx;
}

// Helper to get/set dismissed notifications in localStorage
const getDismissedNotifications = (eventId: string): { actionRequired: boolean; varietyPrompt: boolean } => {
  const key = `votebeats_dismissed_notifications_${eventId}`;
  const stored = localStorage.getItem(key);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return { actionRequired: false, varietyPrompt: false };
    }
  }
  return { actionRequired: false, varietyPrompt: false };
};

const setDismissedNotification = (eventId: string, type: 'actionRequired' | 'varietyPrompt', dismissed: boolean) => {
  const key = `votebeats_dismissed_notifications_${eventId}`;
  const current = getDismissedNotifications(eventId);
  current[type] = dismissed;
  localStorage.setItem(key, JSON.stringify(current));
};

export default function EventManagePage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [nominationCount, setNominationCount] = useState(0);
  const [overflowCount, setOverflowCount] = useState(0);

  const [showPrizePanel, setShowPrizePanel] = useState(false);
  const [showLotteryPanel, setShowLotteryPanel] = useState(false);
  const [showLotteryDirections, setShowLotteryDirections] = useState(false);
  const [showRoulettePanel, setShowRoulettePanel] = useState(false);
  const [showRouletteDirections, setShowRouletteDirections] = useState(false);
  const [rouletteConfig, setRouletteConfig] = useState<RouletteConfig | null>(null);
  const [showDanceOffPanel, setShowDanceOffPanel] = useState(false);
  const [showDanceOffDirections, setShowDanceOffDirections] = useState(false);
  const [openDanceOff, setOpenDanceOff] = useState<DanceOff | null>(null);
  const [showAwardsPanel, setShowAwardsPanel] = useState(false);
  const [showAwardsDirections, setShowAwardsDirections] = useState(false);
  const [eventAwards, setEventAwards] = useState<EventAwards | null>(null);
  const [showPredictionPanel, setShowPredictionPanel] = useState(false);
  const [showPredictionDirections, setShowPredictionDirections] = useState(false);
  const [queuedRequestsForPanel, setQueuedRequestsForPanel] = useState<SongRequest[]>([]);
  const [activePredictionGame, setActivePredictionGame] = useState<PredictionGame | null>(null);
  const [showTriviaPanel, setShowTriviaPanel] = useState(false);
  const [showTriviaDirections, setShowTriviaDirections] = useState(false);
  const [triviaClueCount, setTriviaClueCount] = useState(0);
  const [activeTriviaGame, setActiveTriviaGame] = useState<TriviaGame | null>(null);
  const [prizeCount, setPrizeCount] = useState(0);
  const [actionRequiredDismissed, setActionRequiredDismissed] = useState(false);
  const [varietyPromptDismissed, setVarietyPromptDismissed] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [verifiedCount, setVerifiedCount] = useState(0);
  const [showPostEventPrompt, setShowPostEventPrompt] = useState(false);
  const [postEventForm, setPostEventForm] = useState<PostEventData>({});
  const [savingPostEvent, setSavingPostEvent] = useState(false);
  const [varietyInfo, setVarietyInfo] = useState<{
    isDominant: boolean;
    dominantGenre: string;
    percentage: number;
    missingGenres: string[];
    suggestedGenres: string[];
  } | null>(null);

  useEffect(() => {
    if (!eventId || !user) {
      setLoading(false);
      return;
    }

    const loadEvent = async () => {
      const fetchedEvent = await getEventById(eventId);

      if (!fetchedEvent) {
        // Event not found
        setEvent(null);
        setLoading(false);
        return;
      }

      // Check if current user owns this event
      if (!await isDjEventOwner(eventId, user.uid)) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      setEvent(fetchedEvent);
      setEditMode(fetchedEvent.editMode || false);
      setLoading(false);

      // Trigger post-event data prompt for completed events that haven't had data collected
      if (fetchedEvent.status === 'completed' && !fetchedEvent.postEventData) {
        const skipKey = `votebeats_post_event_skip_${eventId}`;
        if (!sessionStorage.getItem(skipKey)) {
          setShowPostEventPrompt(true);
        }
      }

      // Load dismissed notification state from localStorage
      const dismissed = getDismissedNotifications(eventId);
      setActionRequiredDismissed(dismissed.actionRequired);
      setVarietyPromptDismissed(dismissed.varietyPrompt || false);

      // Count pending requests, nominations, and overflow
      const allRequests = await getRequestsByEventId(eventId);
      const pendingRequests = allRequests.filter(r => r.status === 'pending' && !r.superRequest);
      const pendingNominations = allRequests.filter(r => r.status === 'pending' && r.superRequest && !r.poolApproved);
      setPendingCount(pendingRequests.length);
      setNominationCount(pendingNominations.length);
      setOverflowCount(allRequests.filter(r => r.status === 'overflow').length);

      // Check queue variety (genre distribution)
      const queuedRequests = allRequests.filter(r => r.status === 'queued');
      setQueuedRequestsForPanel(queuedRequests);
      const varietyResult = checkQueueVariety(queuedRequests);
      if (varietyResult && varietyResult.isDominant) {
        const genreDistribution = calculateGenreDistribution(queuedRequests);
        const suggestedGenres = getSuggestedGenres(genreDistribution);
        setVarietyInfo({
          ...varietyResult,
          suggestedGenres,
        });
      } else {
        setVarietyInfo(null);
      }

      // Fetch verified attendee count if on-site verification is enabled
      if (fetchedEvent.settings.onSiteVerificationEnabled) {
        getVerifiedAttendeeCount(eventId).then(setVerifiedCount).catch(() => {});
      }

};

    loadEvent();
  }, [eventId, user]);

  // Real-time listener for voter count, guest tier (ITER-099), Last Song (ITER-175) and end-of-show status (ITER-199) updates
  useEffect(() => {
    if (!eventId) return;
    const unsubscribe = onSnapshot(doc(db, 'events', eventId), (docSnap) => {
      if (!docSnap.exists() || docSnap.data().deletedAt != null) {
        setEvent(null);
        return;
      }
      const data = docSnap.data();
      const liveEvent = docToEvent(docSnap);
      const { guestCount, sizeGate, lastSongRequestId, showStop, endAfterLastSong } = liveEvent;
      const status = computeEventStatus(liveEvent);
      setEvent(prev => {
        if (!prev) return prev;
        const newUniqueVoterCount = data.uniqueVoterCount || undefined;
        const newParticipantCount = typeof data.participantCount === 'number' ? data.participantCount : undefined;
        if (
          prev.uniqueVoterCount === newUniqueVoterCount &&
          prev.participantCount === newParticipantCount &&
          prev.guestCount === guestCount &&
          JSON.stringify(prev.sizeGate) === JSON.stringify(sizeGate) &&
          prev.lastSongRequestId === lastSongRequestId &&
          JSON.stringify(prev.showStop) === JSON.stringify(showStop) &&
          prev.endAfterLastSong === endAfterLastSong &&
          prev.status === status
        ) {
          return prev;
        }
        return { ...prev, uniqueVoterCount: newUniqueVoterCount, participantCount: newParticipantCount, guestCount, sizeGate, lastSongRequestId, showStop, endAfterLastSong, status };
      });
    });
    return () => unsubscribe();
  }, [eventId]);

  // Subscribe to prizes for badge count
  useEffect(() => {
    if (!eventId) return;
    const unsubscribe = subscribeToDJPrizes(eventId, (prizes) => {
      setPrizeCount(prizes.filter(p => p.status !== 'revoked').length);
    });
    return () => unsubscribe();
  }, [eventId]);

  // Subscribe to active prediction game
  useEffect(() => {
    if (!eventId) return;
    const unsubscribe = subscribeToPredictionGame(eventId, setActivePredictionGame);
    return () => unsubscribe();
  }, [eventId]);

  // Subscribe to roulette config (also drives the recurring timer), the open dance-off and the awards reveal
  useEffect(() => {
    if (!eventId || !user) return;
    const unsubRoulette = subscribeToRouletteConfig(eventId, setRouletteConfig, () => {
      toast.error("Couldn't load roulette settings");
    });
    const unsubDanceOff = subscribeToOpenDanceOff(eventId, setOpenDanceOff);
    const unsubAwards = subscribeToEventAwards(eventId, setEventAwards);
    return () => { unsubRoulette(); unsubDanceOff(); unsubAwards(); };
  }, [eventId, user]);

  useRouletteTimer({ eventId, djId: user?.uid, config: rouletteConfig });

  // Subscribe to active trivia game and locked clues
  useEffect(() => {
    if (!eventId) return;
    const unsubGame = subscribeToTriviaGame(eventId, setActiveTriviaGame);
    const unsubClues = subscribeToLockedClues(eventId, (clues) => setTriviaClueCount(clues.length));
    return () => { unsubGame(); unsubClues(); };
  }, [eventId]);

  // Refresh pending count periodically
  useEffect(() => {
    if (!eventId || !event) return;

    const refreshCounts = async () => {
      const allRequests = await getRequestsByEventId(eventId);
      const pendingRequests = allRequests.filter(r => r.status === 'pending' && !r.superRequest);
      const pendingNominations = allRequests.filter(r => r.status === 'pending' && r.superRequest && !r.poolApproved);
      setPendingCount(pendingRequests.length);
      setNominationCount(pendingNominations.length);
      setOverflowCount(allRequests.filter(r => r.status === 'overflow').length);

      // Check queue variety (genre distribution)
      const queuedRequests = allRequests.filter(r => r.status === 'queued');
      setQueuedRequestsForPanel(queuedRequests);
      const varietyResult = checkQueueVariety(queuedRequests);
      if (varietyResult && varietyResult.isDominant) {
        const genreDistribution = calculateGenreDistribution(queuedRequests);
        const suggestedGenres = getSuggestedGenres(genreDistribution);
        setVarietyInfo({
          ...varietyResult,
          suggestedGenres,
        });
      } else {
        setVarietyInfo(null);
      }

    };

    // Refresh every 5 seconds
    const interval = setInterval(refreshCounts, 5000);
    return () => clearInterval(interval);
  }, [eventId, event]);

  // Toggle edit mode
  const handleToggleEditMode = async () => {
    if (!eventId || !event) return;

    try {
      const newEditMode = !editMode;
      await updateEvent(eventId, { editMode: newEditMode });
      setEditMode(newEditMode);
      setEvent({ ...event, editMode: newEditMode });

      if (newEditMode) {
        toast.success('Edit Mode enabled - Attendee queue is frozen');
      } else {
        toast.success('Edit Mode disabled - Attendees can see updated queue');
      }
    } catch {
      toast.error('Failed to toggle edit mode');
    }
  };

  const handleUnlockEvent = async () => {
    if (!eventId || !event) return;
    try {
      await unlockEvent(eventId);
      setEvent({ ...event, locked: false });
      toast.success('Event unlocked');
    } catch {
      toast.error('Failed to unlock event');
    }
  };

  const handleUnarchiveEvent = async () => {
    if (!eventId || !event) return;
    try {
      await unarchiveEvent(eventId);
      setEvent({ ...event, archived: false });
      toast.success('Event moved back to dashboard (still locked)');
    } catch {
      toast.error('Failed to unarchive event');
    }
  };

  const handleSavePostEventData = async () => {
    if (!eventId || !event) return;
    setSavingPostEvent(true);
    try {
      await savePostEventData(eventId, postEventForm);
      setEvent({ ...event, postEventData: { ...postEventForm, collectedAt: new Date().toISOString() } });
      setShowPostEventPrompt(false);
      toast.success('Event details saved');
    } catch {
      toast.error('Failed to save event details');
    } finally {
      setSavingPostEvent(false);
    }
  };

  const handleSkipPostEventPrompt = () => {
    if (eventId) {
      sessionStorage.setItem(`votebeats_post_event_skip_${eventId}`, '1');
    }
    setShowPostEventPrompt(false);
  };

  const navItems = [
    { to: 'queue', icon: ListMusic, label: 'Queue', badge: null },
    { to: 'pending', icon: Clock, label: 'Pending', badge: (pendingCount + (PEOPLES_PICK_HIDDEN ? 0 : nominationCount)) > 0 ? (pendingCount + (PEOPLES_PICK_HIDDEN ? 0 : nominationCount)) : null },
    { to: 'messages', icon: MessageSquare, label: 'Messages', badge: null },
    { to: 'analytics', icon: BarChart3, label: 'Analytics', badge: null },
    { to: 'feedback', icon: Heart, label: 'Feedback', badge: null },
    { to: 'prep', icon: Layers, label: 'Prep Ahead', badge: null },
    { to: 'settings', icon: Settings, label: 'Settings', badge: null },
  ];

  // Derive active tab from URL for display toggling
  const activeTab = (() => {
    const lastSegment = location.pathname.split('/').pop();
    return navItems.some(item => item.to === lastSegment) ? lastSegment! : 'queue';
  })();

  // Get current tab name from URL
  const getCurrentTabName = (): string => {
    const navItem = navItems.find(item => item.to === activeTab);
    return navItem?.label || 'Queue';
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="animate-pulse">
          <Speaker className="w-12 h-12 text-primary" />
        </div>
      </div>
    );
  }

  // Access denied - user doesn't own this event
  if (accessDenied) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-danger" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You don't have permission to manage this event. Only the event creator can access this page.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn btn-primary"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Event not found
  if (!event) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
            <Speaker className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Event Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The event you're looking for doesn't exist or has been deleted.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn btn-primary"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-2 text-sm" aria-label="Breadcrumb">
        <Link
          to="/dashboard"
          className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-primary transition-colors"
        >
          <Home className="w-4 h-4" />
          <span>Dashboard</span>
        </Link>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <span className="text-gray-700 dark:text-gray-300 font-medium truncate max-w-[200px]" title={event.name}>
          {event.name}
        </span>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <span className="text-primary font-medium">
          {getCurrentTabName()}
        </span>
      </nav>

      {/* Header */}
      <div className="flex items-center gap-4 min-w-0">
        <button
          onClick={() => navigate('/dashboard')}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex-shrink-0"
          aria-label="Back to dashboard"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate min-w-0 max-w-full" title={event.name}>{event.name}</h1>
            {event.sizeGate?.applies && <EventTierBadge guestCount={event.guestCount ?? 0} sizeGate={event.sizeGate} />}
          </div>
          <p className="text-gray-600 dark:text-gray-400 truncate">Event ID: {eventId}</p>
        </div>
        <button
          onClick={() => !activePredictionGame && setShowPredictionPanel(true)}
          className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex-shrink-0"
          title={activePredictionGame ? 'Prediction game active — see banner below' : 'Start Prediction Game'}
        >
          <Crosshair className={`w-5 h-5 ${activePredictionGame ? 'text-green-500' : 'text-gray-400'}`} />
          {activePredictionGame && (
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-500" />
          )}
        </button>
        <button
          onClick={() => setShowTriviaPanel(true)}
          className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex-shrink-0"
          title={activeTriviaGame ? 'Trivia game active' : 'Trivia Game'}
        >
          <Brain className={`w-5 h-5 ${activeTriviaGame ? 'text-purple-500' : 'text-gray-400'}`} />
          {activeTriviaGame && (
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-purple-500" />
          )}
          {!activeTriviaGame && triviaClueCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 px-1 py-0.5 text-[9px] font-bold rounded-full bg-purple-500 text-white min-w-[1rem] text-center">
              {triviaClueCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setShowLotteryPanel(true)}
          className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex-shrink-0"
          title="Lottery"
        >
          <Ticket className={`w-5 h-5 ${event.settings.lotteryEnabled ? 'text-green-500' : 'text-gray-400'}`} />
          {event.settings.lotteryEnabled && (
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-500" />
          )}
        </button>
        <button
          onClick={() => setShowRoulettePanel(true)}
          className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex-shrink-0"
          title={rouletteConfig?.enabled ? 'Request Roulette running' : 'Request Roulette'}
        >
          <Dices className={`w-5 h-5 ${rouletteConfig?.enabled ? 'text-green-500' : 'text-gray-400'}`} />
          {rouletteConfig?.enabled && (
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-500" />
          )}
        </button>
        <button
          onClick={() => setShowDanceOffPanel(true)}
          className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex-shrink-0"
          title={openDanceOff ? 'Dance-off voting open' : 'Dance-off'}
        >
          <PartyPopper className={`w-5 h-5 ${openDanceOff ? 'text-pink-500' : 'text-gray-400'}`} />
          {openDanceOff && (
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-pink-500" />
          )}
        </button>
        <button
          onClick={() => setShowAwardsPanel(true)}
          className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex-shrink-0"
          title={eventAwards?.status === 'revealing' ? 'Awards reveal running' : 'Event Awards'}
        >
          <Award className={`w-5 h-5 ${eventAwards?.status === 'revealing' ? 'text-amber-500' : 'text-gray-400'}`} />
          {eventAwards?.status === 'revealing' && (
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-amber-500" />
          )}
        </button>
        {prizeCount > 0 && (
          <button
            onClick={() => setShowPrizePanel(true)}
            className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex-shrink-0"
            title="Manage Prizes"
          >
            <Trophy className="w-5 h-5 text-amber-500" />
            <span className="absolute -top-0.5 -right-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-amber-500 text-white min-w-[1.25rem] text-center">
              {prizeCount}
            </span>
          </button>
        )}
      </div>

      {/* On-Site Verification Active Badge */}
      {event.settings.onSiteVerificationEnabled && (
        <div className="flex items-center gap-2 px-3 py-2 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg">
          <Shield className="w-4 h-4 text-teal-600 dark:text-teal-400" />
          <span className="text-sm font-medium text-teal-700 dark:text-teal-300">
            On-Site Mode Active
          </span>
          <span className="text-sm text-teal-600 dark:text-teal-400">
            ({verifiedCount} verified)
          </span>
        </div>
      )}

      {/* Prediction Game Inline Banner */}
      {activePredictionGame && eventId && (
        <PredictionGameBanner
          game={activePredictionGame}
          setEvent={setEvent}
          queuedRequests={queuedRequestsForPanel}
        />
      )}

      {/* Locked / Archived Banner */}
      {event.locked && (
        <div className="flex items-center justify-between p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-3">
            <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-200">
                {event.archived ? 'This event is archived (view-only)' : 'This event is locked (view-only)'}
              </p>
              <p className="text-sm text-amber-600 dark:text-amber-400">All editing is disabled.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {event.status === 'completed' && !event.postEventData && (
              <button
                onClick={() => setShowPostEventPrompt(true)}
                className="px-3 py-1.5 text-sm rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/60 transition-colors"
              >
                Add Event Details
              </button>
            )}
            {event.archived ? (
              <button
                onClick={handleUnarchiveEvent}
                className="px-3 py-1.5 text-sm rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors"
              >
                Unarchive
              </button>
            ) : (
              <button
                onClick={handleUnlockEvent}
                className="px-3 py-1.5 text-sm rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors"
              >
                Unlock
              </button>
            )}
          </div>
        </div>
      )}

      {/* Action Required Banner */}
      {(pendingCount > 0 || (!PEOPLES_PICK_HIDDEN && nominationCount > 0)) && !actionRequiredDismissed && (
        <div
          className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
            pendingCount >= 10
              ? 'bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800'
              : pendingCount >= 5
              ? 'bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800'
              : 'bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800'
          }`}
          role="alert"
        >
          <Link
            to="pending"
            className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity"
            aria-label={`${pendingCount} pending requests need review`}
          >
            <div className={`p-2 rounded-full ${
              pendingCount >= 10
                ? 'bg-red-200 dark:bg-red-800'
                : pendingCount >= 5
                ? 'bg-amber-200 dark:bg-amber-800'
                : 'bg-blue-200 dark:bg-blue-800'
            }`}>
              <AlertCircle className={`w-5 h-5 ${
                pendingCount >= 10
                  ? 'text-red-600 dark:text-red-400'
                  : pendingCount >= 5
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-blue-600 dark:text-blue-400'
              }`} />
            </div>
            <div>
              <p className={`font-medium ${
                pendingCount >= 10
                  ? 'text-red-800 dark:text-red-200'
                  : pendingCount >= 5
                  ? 'text-amber-800 dark:text-amber-200'
                  : 'text-blue-800 dark:text-blue-200'
              }`}>
                Action Required
              </p>
              <p className={`text-sm ${
                pendingCount >= 10
                  ? 'text-red-600 dark:text-red-400'
                  : pendingCount >= 5
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-blue-600 dark:text-blue-400'
              }`}>
                {pendingCount + (PEOPLES_PICK_HIDDEN ? 0 : nominationCount)} pending {!PEOPLES_PICK_HIDDEN && pendingCount > 0 && nominationCount > 0 ? 'request & nomination' : !PEOPLES_PICK_HIDDEN && nominationCount > 0 ? 'nomination' : 'request'}{(pendingCount + (PEOPLES_PICK_HIDDEN ? 0 : nominationCount)) !== 1 ? 's' : ''} need{(pendingCount + (PEOPLES_PICK_HIDDEN ? 0 : nominationCount)) === 1 ? 's' : ''} review
              </p>
            </div>
            <ChevronRight className={`w-5 h-5 ${
              pendingCount >= 10
                ? 'text-red-600 dark:text-red-400'
                : pendingCount >= 5
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-blue-600 dark:text-blue-400'
            }`} />
          </Link>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setActionRequiredDismissed(true);
              if (eventId) {
                setDismissedNotification(eventId, 'actionRequired', true);
              }
            }}
            className={`ml-2 p-1.5 rounded-full hover:bg-opacity-50 transition-colors ${
              pendingCount >= 10
                ? 'hover:bg-red-200 dark:hover:bg-red-800'
                : pendingCount >= 5
                ? 'hover:bg-amber-200 dark:hover:bg-amber-800'
                : 'hover:bg-blue-200 dark:hover:bg-blue-800'
            }`}
            aria-label="Dismiss action required notification"
          >
            <X className={`w-5 h-5 ${
              pendingCount >= 10
                ? 'text-red-600 dark:text-red-400'
                : pendingCount >= 5
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-blue-600 dark:text-blue-400'
            }`} />
          </button>
        </div>
      )}

      {/* Overflow Pool Info */}
      {OVERFLOW_POOL_ENABLED && event?.settings?.overflowEnabled && overflowCount > 0 && (
        <div className="flex items-center gap-3 p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
          <div className="p-1.5 bg-indigo-200 dark:bg-indigo-800 rounded-full">
            <ListMusic className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <p className="text-sm text-indigo-700 dark:text-indigo-400">
            <span className="font-medium">{overflowCount}</span> overflow song{overflowCount !== 1 ? 's' : ''} waiting — raise the request limit from the Queue tab to release them.
          </p>
        </div>
      )}

      {/* Variety Prompt - when queue is too homogeneous */}
      {varietyInfo && varietyInfo.isDominant && !varietyPromptDismissed && (
        <div
          className="flex items-center justify-between p-4 rounded-lg transition-colors bg-purple-100 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800"
          role="alert"
        >
          <div className="flex items-center gap-3 flex-1">
            <div className="p-2 rounded-full bg-purple-200 dark:bg-purple-800">
              <Palette className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-purple-800 dark:text-purple-200">
                Queue Variety Alert
              </p>
              <p className="text-sm text-purple-600 dark:text-purple-400">
                Your queue is {varietyInfo.percentage}% {varietyInfo.dominantGenre}. Consider adding variety!
              </p>
              {varietyInfo.suggestedGenres.length > 0 && (
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className="text-xs text-purple-600 dark:text-purple-400">Try adding:</span>
                  {varietyInfo.suggestedGenres.map((genre) => (
                    <span
                      key={genre}
                      className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200"
                      style={{
                        borderLeft: `3px solid ${GENRE_COLORS[genre] || GENRE_COLORS['Other']}`,
                      }}
                    >
                      {genre}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setVarietyPromptDismissed(true);
              if (eventId) {
                setDismissedNotification(eventId, 'varietyPrompt', true);
              }
            }}
            className="ml-2 p-1.5 rounded-full hover:bg-purple-200 dark:hover:bg-purple-800 transition-colors"
            aria-label="Dismiss variety prompt notification"
          >
            <X className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </button>
        </div>
      )}

      {/* Edit Mode Toggle */}
      <div
        className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
          editMode
            ? 'bg-orange-100 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800'
            : 'bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${
            editMode
              ? 'bg-orange-200 dark:bg-orange-800'
              : 'bg-gray-200 dark:bg-gray-700'
          }`}>
            {editMode ? (
              <Lock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            ) : (
              <Unlock className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            )}
          </div>
          <div>
            <p className={`font-medium ${
              editMode
                ? 'text-orange-800 dark:text-orange-200'
                : 'text-gray-700 dark:text-gray-300'
            }`}>
              {editMode ? 'Edit Mode Active' : 'Edit Mode'}
            </p>
            <p className={`text-sm ${
              editMode
                ? 'text-orange-600 dark:text-orange-400'
                : 'text-gray-500 dark:text-gray-400'
            }`}>
              {editMode
                ? 'Attendee queue is frozen while you reorganize'
                : 'Enable to freeze attendee view while reorganizing queue'}
            </p>
          </div>
        </div>
        <button
          onClick={handleToggleEditMode}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            editMode
              ? 'bg-orange-500 hover:bg-orange-600 text-white'
              : 'bg-primary hover:bg-primary-dark text-white'
          }`}
        >
          {editMode ? 'Disable Edit Mode' : 'Enable Edit Mode'}
        </button>
      </div>

      {/* Navigation Tabs */}
      <nav className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-x-auto">
        {navItems.map(({ to, icon: Icon, label, badge }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                isActive
                  ? 'bg-white dark:bg-gray-900 text-primary shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`
            }
          >
            <Icon className="w-4 h-4" />
            <span>{label}</span>
            {badge !== null && (
              <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold rounded-full bg-danger text-white min-w-[1.25rem] text-center">
                {badge > 99 ? '99+' : badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Content — all tabs rendered simultaneously, toggled via display */}
      <EventManageContext.Provider value={{ event, setEvent }}>
        <div className="card p-6" style={{ display: activeTab === 'queue' ? undefined : 'none' }}>
          <QueuePage />
        </div>
        <div className="card p-6" style={{ display: activeTab === 'pending' ? undefined : 'none' }}>
          <PendingPage />
        </div>
        <div className="card p-6" style={{ display: activeTab === 'messages' ? undefined : 'none' }}>
          <MessagesPage />
        </div>
        <div className="card p-6" style={{ display: activeTab === 'analytics' ? undefined : 'none' }}>
          <AnalyticsPage />
        </div>
        <div className="card p-6" style={{ display: activeTab === 'feedback' ? undefined : 'none' }}>
          <FeedbackPage />
        </div>
        <div className="card p-6" style={{ display: activeTab === 'settings' ? undefined : 'none' }}>
          <EventSettingsPage />
        </div>
        <div className="card p-6" style={{ display: activeTab === 'prep' ? undefined : 'none' }}>
          <PrepAheadPage />
        </div>
      </EventManageContext.Provider>

      {/* Prize Management Modal */}
      {showPrizePanel && eventId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white dark:bg-gray-900 rounded-t-xl sm:rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Prize Management</h2>
              <button
                onClick={() => setShowPrizePanel(false)}
                className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <PrizeManagePanel eventId={eventId} />
          </div>
        </div>
      )}

      {/* Lottery Panel Modal */}
      {showLotteryPanel && eventId && event && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white dark:bg-gray-900 rounded-t-xl sm:rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Lottery</h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowLotteryDirections(true)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  title="How it works"
                  aria-label="Game directions"
                >
                  <Info className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowLotteryPanel(false)}
                  className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            <LotteryPanel eventId={eventId} djId={user!.uid} event={event} setEvent={setEvent} />
          </div>
        </div>
      )}
      {showLotteryDirections && (
        <GameDirectionsModal gameKey="lottery" onClose={() => setShowLotteryDirections(false)} />
      )}

      {/* Request Roulette Panel Modal (ITER-020) */}
      {showRoulettePanel && eventId && event && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white dark:bg-gray-900 rounded-t-xl sm:rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Request Roulette</h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowRouletteDirections(true)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  title="How it works"
                  aria-label="Game directions"
                >
                  <Info className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowRoulettePanel(false)}
                  className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            <RoulettePanel eventId={eventId} djId={user!.uid} config={rouletteConfig} />
          </div>
        </div>
      )}
      {showRouletteDirections && (
        <GameDirectionsModal gameKey="roulette" onClose={() => setShowRouletteDirections(false)} />
      )}

      {/* Dance-off Panel Modal (ITER-022) */}
      {showDanceOffPanel && eventId && event && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white dark:bg-gray-900 rounded-t-xl sm:rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Dance-off</h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowDanceOffDirections(true)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  title="How it works"
                  aria-label="Game directions"
                >
                  <Info className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowDanceOffPanel(false)}
                  className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            <DanceOffPanel eventId={eventId} djId={user!.uid} event={event} />
          </div>
        </div>
      )}
      {showDanceOffDirections && (
        <GameDirectionsModal gameKey="dance_off" onClose={() => setShowDanceOffDirections(false)} />
      )}

      {/* Event Awards Panel Modal (ITER-041) */}
      {showAwardsPanel && eventId && event && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white dark:bg-gray-900 border border-transparent dark:border-gray-600 rounded-t-xl sm:rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Event Awards</h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowAwardsDirections(true)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  title="How it works"
                  aria-label="Game directions"
                >
                  <Info className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowAwardsPanel(false)}
                  className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                  aria-label="Close"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            <EventAwardsPanel eventId={eventId} djId={user!.uid} event={event} />
          </div>
        </div>
      )}
      {showAwardsDirections && (
        <GameDirectionsModal gameKey="awards" onClose={() => setShowAwardsDirections(false)} />
      )}

      {/* Prediction Panel Modal (setup only — no active game) */}
      {showPredictionPanel && !activePredictionGame && eventId && event && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white dark:bg-gray-900 rounded-t-xl sm:rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Prediction Game</h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowPredictionDirections(true)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  title="How it works"
                  aria-label="Game directions"
                >
                  <Info className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowPredictionPanel(false)}
                  className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            <PredictionPanel eventId={eventId} djId={user!.uid} event={event} setEvent={setEvent} onCreated={() => setShowPredictionPanel(false)} />
          </div>
        </div>
      )}
      {showPredictionDirections && (
        <GameDirectionsModal gameKey="prediction" onClose={() => setShowPredictionDirections(false)} />
      )}

      {/* Trivia Panel Modal */}
      {showTriviaPanel && eventId && event && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white dark:bg-gray-900 rounded-t-xl sm:rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Trivia Game</h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowTriviaDirections(true)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  title="How it works"
                  aria-label="Game directions"
                >
                  <Info className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowTriviaPanel(false)}
                  className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            <TriviaPanel eventId={eventId} djId={user!.uid} event={event} setEvent={setEvent} onCreated={() => setShowTriviaPanel(false)} />
          </div>
        </div>
      )}
      {showTriviaDirections && (
        <GameDirectionsModal gameKey="trivia" onClose={() => setShowTriviaDirections(false)} />
      )}

      {/* Post-Event Data Modal */}
      {showPostEventPrompt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <span>🎉</span> Event Complete!
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Help improve your analytics by filling in a few details.
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Guest Count
                </label>
                <input
                  type="number"
                  min={0}
                  placeholder="How many people attended?"
                  value={postEventForm.guestCount ?? ''}
                  onChange={(e) => setPostEventForm(f => ({ ...f, guestCount: e.target.value ? Number(e.target.value) : undefined }))}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Venue Name
                </label>
                <input
                  type="text"
                  placeholder="e.g., Community Center"
                  value={postEventForm.venueName ?? ''}
                  onChange={(e) => setPostEventForm(f => ({ ...f, venueName: e.target.value || undefined }))}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Venue Type
                </label>
                <select
                  value={postEventForm.venueType ?? ''}
                  onChange={(e) => setPostEventForm(f => ({ ...f, venueType: e.target.value || undefined }))}
                  className="input w-full"
                >
                  <option value="">Select type...</option>
                  <option value="School">School</option>
                  <option value="Church">Church</option>
                  <option value="Club">Club</option>
                  <option value="Private Venue">Private Venue</option>
                  <option value="Outdoor">Outdoor</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Actual Duration (minutes)
                </label>
                <input
                  type="number"
                  min={0}
                  placeholder="How long did the event run?"
                  value={postEventForm.actualDurationMinutes ?? ''}
                  onChange={(e) => setPostEventForm(f => ({ ...f, actualDurationMinutes: e.target.value ? Number(e.target.value) : undefined }))}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Notes
                </label>
                <textarea
                  rows={3}
                  placeholder="Any observations about the event?"
                  value={postEventForm.notes ?? ''}
                  onChange={(e) => setPostEventForm(f => ({ ...f, notes: e.target.value || undefined }))}
                  className="input w-full resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end pt-2">
              <button onClick={handleSkipPostEventPrompt} className="btn btn-secondary">
                Skip for now
              </button>
              <button onClick={handleSavePostEventData} disabled={savingPostEvent} className="btn btn-primary">
                {savingPostEvent ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
